import { Component, OnInit, OnDestroy } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Location } from '@angular/common';
import { ActivatedRoute } from '@angular/router';
import { Subscription } from 'rxjs';
import { MatDialog } from '@angular/material/dialog';
import { GlobalSearchService } from '../../services/globalsearchservice.service';
import {
	formatPaymentRunPostedMessage,
	hasPaymentRunPostings,
	paymentRunPostingsCount,
	PaymentsService
} from '../../services/payments.service';
import { VendorService } from '../../services/vendor.service';
import { PrintService } from '../../services/print.service';
import { FormBuilder, FormGroup } from '@angular/forms';
import {
	VendorRemitPreviewModalComponent,
	VendorRemitPreviewModalData,
	VendorRemitPreviewTabRequest
} from '../../shared/vendor-remit-preview-modal/vendor-remit-preview-modal.component';

function checkRunLocalYmd(d: Date): string {
	return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

function checkRunAddDays(d: Date, days: number): Date {
	const t = new Date(d.getTime());
	t.setDate(t.getDate() + days);
	return t;
}

export enum PaymentRunWorkflow {
	Initial = 'initial',
	Generating = 'generating',
	Generated = 'generated',
	Posting = 'posting',
	Posted = 'posted',
	SavedForReview = 'saved_for_review'
}

@Component({
	selector: 'app-check-printing',
	templateUrl: './check-printing.component.html',
	styleUrls: ['./check-printing.component.scss']
})
export class CheckPrintingComponent implements OnInit, OnDestroy {
	readonly PaymentRunWorkflow = PaymentRunWorkflow;
	private routeSub: Subscription | null = null;
	currencies: any[] = [];
	bankAccounts: any[] = [];
	paymentMethods: any[] = [];
	vendorTypes: any[] = [];
	dayPayments: any[] = [];

	postingDate: string = checkRunLocalYmd(new Date());
	/** Full vendor list for mat-select (filtered by vendor type). */
	vendorSelectList: { supplierid: string; suppname: string; supptype: string | number }[] = [];
	vendorSelectSearch = '';
	loadingVendors = false;
	includeDiscount = '1';

	color = 'blue';

	paymentForm: FormGroup;
	checkData: any = [];
	checklist: any = {};
	vendorTotals: { [key: string]: number } = {};
	/** Per-vendor statement header figures (Past Due / Current / Total Amount Due …)
	 *  populated only when loaded via "Load statement view". */
	statementSummary: { [key: string]: any } = {};
	/** Reconciliation tools (Load statement view / Select Amount Due) are hidden in the
	 *  UI by default — set true to re-enable the buttons. */
	showStatementTools = false;
	data: any = [];
	totalDue: any = 0;
	totalDiscount = 0;
	selectedTransactions = 0;
	vendorChecks: {
		[key: string]: boolean } = {};
	vendorDates: {
		[key: string]: string } = {};
	vendorPaymentTypes: {
		[key: string]: string } = {};
	vendorCheckNumbers: {
		[key: string]: string } = {};
	/** Vendors whose Ref # the clerk typed (or that came back from a saved run). Auto
	 *  numbering never overwrites these, and never hands their number to anyone else. */
	vendorCheckNumberEdited: {
		[key: string]: boolean } = {};
	checks: any = [];
	responsePayload: any = [];
	workflow: PaymentRunWorkflow = PaymentRunWorkflow.Initial;
	/** User-visible banner (save for review or posted). */
	bannerMessage: string | null = null;
	/** Last save-for-review journal (for Pending runs link timeout). */
	savedForReviewJournal: number | null = null;
	/** Finalize API payload after Approve & post (summary + journal). */
	postedResult: any = null;
	/** Run journal successfully finalized in this session — blocks double post for same journal until Search / load another run. */
	lastPostedRunJournal: number | string | null = null;
	loadedJournal: number | null = null;
	loadedPostings: any = null;
	actionInProgress = false;
	exportingChecklistXls = false;
	exportingChecklistPdf = false;
	config: any = null;

	/** Which collapsed vendor groups are currently open. Default: all open after search. */
	expandedVendors: { [key: string]: boolean } = {};
	vendorRowFilters: {
		[key: string]: {
			showInvoices: boolean;
			showCredits: boolean;
			useInvoices: boolean;
			useCredits: boolean;
		}
	} = {};
	globalRowFilters = {
		showInvoices: true,
		showCredits: true,
		useInvoices: true,
		useCredits: true
	};
	vendorSearchTerms: { [key: string]: string } = {};

	/** Filter form visibility. Auto-collapses on successful search; reopens via Edit filters. */
	filtersExpanded = true;

	/** Live posting progress during a chunked Approve & post. null when idle. */
	postingProgress: {
		current: number;
		total: number;
		posted: number;
		skipped: number;
		errors: any[];
	} | null = null;

	/** Chunk size when posting — keeps individual requests under ~10s on the server,
	    gives the clerk visible progress, and lets a long run resume cleanly on retry. */
	private readonly POST_CHUNK_SIZE = 10; // 10 vendors/request keeps each call under ~5s; 158 vendors = 16 requests

	/** Can the user jump to this step from where they are now? Only earlier steps
	    in the same run are reachable (a "previous" step). 'posted' steps are
	    historical — clicking step 1 from posted starts a brand new run. */
	canNavigateToPhase(target: 'select' | 'review' | 'posted'): boolean {
		const order = ['select', 'review', 'posted'];
		const current = order.indexOf(this.currentPhase);
		const targetIdx = order.indexOf(target);
		return targetIdx >= 0 && targetIdx < current;
	}

	/** Click on a phase step: regress to that step if it's "previous" to current. */
	onPhaseClick(target: 'select' | 'review' | 'posted'): void {
		if (this.actionInProgress) {return;}
		if (!this.canNavigateToPhase(target)) {return;}
		if (target === 'select') {
			if (this.currentPhase === 'review') {
				this.backToEditRun();
			} else if (this.currentPhase === 'posted') {
				// Posted runs are finalized; clicking step 1 starts a fresh run.
				this.checks = [];
				this.responsePayload = [];
				this.loadedJournal = null;
				this.resetPaymentRunBannerState();
				this.filtersExpanded = true;
				this.globalSearchService.showNotification('Starting a new payment run.', 'info', 'bottom', 'right');
			}
			return;
		}
		if (target === 'review' && this.currentPhase === 'posted') {
			// Stay on the same view — the check cards are still there. Just nudge the user.
			this.globalSearchService.showNotification('This run is already posted — viewing the printed checks.', 'info', 'bottom', 'right');
		}
	}

	/** Three-phase view: Select invoices › Review checks › Posted. Derived from workflow. */
	get currentPhase(): 'select' | 'review' | 'posted' {
		if (this.workflow === PaymentRunWorkflow.Posted) {return 'posted';}
		if (this.workflow === PaymentRunWorkflow.Generated
			|| this.workflow === PaymentRunWorkflow.Generating
			|| this.workflow === PaymentRunWorkflow.Posting) {return 'review';}
		return 'select';
	}

	/** Generated check preview exists (array after formatChecks, or legacy object map). */
	get hasGeneratedChecks(): boolean {
		const c = this.checks;
		if (Array.isArray(c)) {return c.length > 0;}
		if (c && typeof c === 'object') {return Object.keys(c).length > 0;}
		return false;
	}

	get checkCount(): number {
		const c = this.checks;
		if (Array.isArray(c)) {return c.length;}
		if (c && typeof c === 'object') {return Object.keys(c).length;}
		return 0;
	}

	/** Sticky footer — select grid, generated review, or posted recap. */
	get showStickyFooter(): boolean {
		if (this.currentPhase === 'review' && this.hasGeneratedChecks) {return true;}
		if (this.currentPhase === 'posted' && (this.hasGeneratedChecks || this.postedResult)) {return true;}
		if (this.currentPhase === 'select' && this.data?.success && this.checklist && Object.keys(this.checklist).length) {
			return true;
		}
		return false;
	}

	/** Total for review/posted sticky footer when select-phase totalDue was cleared on generate. */
	get reviewRunTotal(): number {
		const c = this.checks;
		if (Array.isArray(c)) {
			return c.reduce((sum, row) => sum + (Number(row?.postings?.AccumBalance) || 0), 0);
		}
		if (c && typeof c === 'object') {
			return Object.values(c).reduce<number>(
				(sum, row: any) => sum + (Number(row?.postings?.AccumBalance) || 0),
				0
			);
		}
		return Number(this.totalDue) || 0;
	}

	get stickyFooterTotal(): number {
		if (this.currentPhase === 'select') {return Number(this.totalDue) || 0;}
		if (this.postedResult?.summary?.totalAmount != null) {return Number(this.postedResult.summary.totalAmount);}
		return this.reviewRunTotal;
	}

	/** Format money for confirmation copy ('en-US' is fine — currency code surfaced separately). */
	private fmtMoney(n: number, currency = 'USD'): string {
		try {
			return new Intl.NumberFormat('en-US', { style: 'currency', currency }).format(n || 0);
		} catch {
			return '$' + (n || 0).toFixed(2);
		}
	}

	/** Toggle one vendor's collapsed state. */
	toggleVendor(key: string): void {
		this.expandedVendors[key] = !this.expandedVendors[key];
	}

	/** Format a transaction date for the row. Rejects sentinels and any year < 1900. */
	formatTxnDate(s: string | null | undefined): string {
		if (!s) {return '—';}
		const str = String(s).trim();
		if (!str || str === '0' || str.startsWith('-') || str.startsWith('0000')) {return '—';}
		// MM/DD/YYYY (or MM/DD/-1, MM/DD/12, etc. — validate the year).
		const mdy = /^(\d{1,2})\/(\d{1,2})\/(-?\d+)$/.exec(str);
		if (mdy) {
			const year = parseInt(mdy[3], 10);
			if (!isFinite(year) || year < 1900 || year > 2100) {return '—';}
			return `${mdy[1].padStart(2, '0')}/${mdy[2].padStart(2, '0')}/${year}`;
		}
		// YYYY-MM-DD
		const ymd = /^(\d{4})-(\d{2})-(\d{2})/.exec(str);
		if (ymd) {
			const year = parseInt(ymd[1], 10);
			if (year < 1900 || year > 2100) {return '—';}
			return `${ymd[2]}/${ymd[3]}/${year}`;
		}
		return '—';
	}

	/** Invoice/ref cell text: prefer suppreference, fall back to the transaction type. */
	formatTxnRef(t: any): string {
		const ref = (t?.suppreference || '').toString().trim();
		if (ref) {return ref;}
		// "Creditors Payment · #5419" → keep the meta line; the ref line just shows the type bold.
		const typename = (t?.typename || '').toString().trim();
		return typename || `#${t?.transno || ''}`;
	}

	isCreditTxn(t: any): boolean {
		const type = String(t?.type ?? '').trim();
		const typename = String(t?.typename ?? '').toLowerCase();
		const bal = Number(t?.bal ?? t?.balance ?? 0);
		const total = Number(t?.ovamount || 0) + Number(t?.ovgst || 0) + Number(t?.ovfee || 0);
		return type === '21' || typename.includes('credit') || bal < 0 || total < 0;
	}

	txnTypeLabel(t: any): 'Credit' | 'Invoice' {
		return this.isCreditTxn(t) ? 'Credit' : 'Invoice';
	}

	/**
	 * Already settled in the ERP. The statement line still carries a balance, so
	 * bal <= 0 does NOT catch these — check the ERP side explicitly.
	 * 'settled_apc0430' = pre-05/01/26 non-cash AP cleanup.
	 */
	isPaidTxn(t: any): boolean {
		const status = String(t?.stmt_status ?? '').toLowerCase();
		return Number(t?.erp_settled ?? 0) === 1
			|| status === 'paid_since'
			|| status === 'settled_apc0430';
	}

	paidTxnLabel(t: any): string {
		return String(t?.stmt_status ?? '').toLowerCase() === 'settled_apc0430'
			? 'Settled pre-05/01/26'
			: 'Already paid';
	}

	/** Paid rows in a vendor group — drives the "x already paid" header hint. */
	vendorPaidCount(rows: any[]): number {
		return (rows || []).filter((t: any) => this.isPaidTxn(t)).length;
	}

	private ensureVendorRowFilter(vendorKey: string): void {
		if (!this.vendorRowFilters[vendorKey]) {
			this.vendorRowFilters[vendorKey] = { ...this.globalRowFilters };
		}
	}

	private searchableTxnText(t: any): string {
		return [
			this.formatTxnRef(t),
			t?.suppreference,
			t?.typename,
			t?.transno,
			t?.trandate,
			t?.duedate,
			t?.bal,
			t?.ovamount,
			t?.ovgst,
			t?.ovfee
		].map(v => String(v ?? '')).join(' ').toLowerCase();
	}

	private vendorSearchTerm(vendorKey: string): string {
		return (this.vendorSearchTerms[vendorKey] || '').trim();
	}

	transactionMatchesSearch(t: any, vendorKey: string): boolean {
		const q = this.vendorSearchTerm(vendorKey).toLowerCase();
		return q === '' || this.searchableTxnText(t).includes(q);
	}

	visibleVendorTransactions(vendorKey: string, rows: any[]): any[] {
		this.ensureVendorRowFilter(vendorKey);
		const f = this.vendorRowFilters[vendorKey];
		return (rows || []).filter(t => {
			const credit = this.isCreditTxn(t);
			if (credit && !f.showCredits) {return false;}
			if (!credit && !f.showInvoices) {return false;}
			return this.transactionMatchesSearch(t, vendorKey);
		});
	}

	vendorSearchMatchCount(vendorKey: string): number {
		const rows = (this.checklist?.[vendorKey] || []) as any[];
		if (!this.vendorSearchTerm(vendorKey)) {return 0;}
		return rows.filter(t => this.transactionMatchesSearch(t, vendorKey)).length;
	}

	highlightSegments(value: any, vendorKey: string): Array<{ text: string; match: boolean }> {
		const text = String(value ?? '');
		const q = this.vendorSearchTerm(vendorKey);
		if (!q) {return [{ text, match: false }];}
		const lower = text.toLowerCase();
		const needle = q.toLowerCase();
		const out: Array<{ text: string; match: boolean }> = [];
		let pos = 0;
		while (pos < text.length) {
			const idx = lower.indexOf(needle, pos);
			if (idx === -1) {
				out.push({ text: text.slice(pos), match: false });
				break;
			}
			if (idx > pos) {out.push({ text: text.slice(pos, idx), match: false });}
			out.push({ text: text.slice(idx, idx + q.length), match: true });
			pos = idx + q.length;
		}
		return out.length ? out : [{ text, match: false }];
	}

	onVendorSearchChange(vendorKey: string): void {
		if (this.vendorSearchTerm(vendorKey)) {
			this.expandedVendors[vendorKey] = true;
		}
	}

	clearVendorSearch(vendorKey: string): void {
		this.vendorSearchTerms[vendorKey] = '';
	}

	/** Render money as fixed-2 string. Used to pre-format bal/disc when results land. */
	private toMoneyStr(n: any): string {
		const v = Number(n);
		if (!isFinite(v)) {return '0.00';}
		return (Math.round(v * 100) / 100).toFixed(2);
	}

	/** Click-anywhere-on-row → toggle selection (skipped on input/button clicks). */
	toggleTxn(transaction: any, vendorKey: string, ev?: MouseEvent): void {
		if (ev) {
			const t = ev.target as HTMLElement;
			if (t && (t.tagName === 'INPUT' || t.tagName === 'BUTTON' || t.closest('input, button, label'))) {
				return;
			}
		}
		if (this.isPaidTxn(transaction)) {return;}   // can't pay what's already settled
		transaction.selected = !transaction.selected;
		this.updateVendorTotal(vendorKey);
	}

	/** Selected count + total balance for a vendor (for the collapsed header). */
	vendorRowSelectedCount(key: string): number {
		const rows = (this.checklist?.[key] || []) as any[];
		return rows.filter(r => r.selected).length;
	}

	applyGlobalRowFilter(kind: 'showInvoices' | 'showCredits' | 'useInvoices' | 'useCredits', checked: boolean): void {
		this.globalRowFilters[kind] = checked;
		if (!this.checklist || typeof this.checklist !== 'object') {return;}
		Object.keys(this.checklist).forEach(vendorKey => {
			this.ensureVendorRowFilter(vendorKey);
			this.vendorRowFilters[vendorKey][kind] = checked;
			if (kind === 'useInvoices' || kind === 'useCredits') {
				this.applyVendorUseFilter(vendorKey, kind, checked, false);
			}
		});
		this.updateTotals();
	}

	onVendorRowFilterChange(vendorKey: string, kind: 'showInvoices' | 'showCredits' | 'useInvoices' | 'useCredits', checked: boolean): void {
		this.ensureVendorRowFilter(vendorKey);
		this.vendorRowFilters[vendorKey][kind] = checked;
		if (kind === 'useInvoices' || kind === 'useCredits') {
			this.applyVendorUseFilter(vendorKey, kind, checked, true);
		}
	}

	private applyVendorUseFilter(vendorKey: string, kind: 'useInvoices' | 'useCredits', checked: boolean, update = true): void {
		const rows = (this.checklist?.[vendorKey] || []) as any[];
		const applyToCredits = kind === 'useCredits';
		rows.forEach(t => {
			if (this.isCreditTxn(t) === applyToCredits) {
				t.selected = checked;
			}
		});
		this.vendorChecks[vendorKey] = rows.length > 0 && rows.every(t => t.selected);
		if (update) {this.updateVendorTotal(vendorKey);}
	}

	/** A vendor is "blocked" when at least one row is selected but the net total is
	    non-positive — server can't post that, so we surface it inline before Generate. */
	vendorBlockedReason(key: string): string | null {
		const rows = (this.checklist?.[key] || []) as any[];
		const selected = rows.filter(r => r.selected);
		if (!selected.length) {return null;}
		const net = Number(this.vendorTotals?.[key]) || 0;
		if (net < 0) {return `Net ${this.fmtMoney(net)} — credit exceeds invoices. Deselect the credit or add invoices to cover it.`;}
		if (net === 0) {return 'Net is exactly $0.00 — no check to write. Deselect this vendor or adjust amounts.';}
		return null;
	}

	/** All vendor keys whose selection nets ≤ 0. Used to disable Generate. */
	get blockedVendorKeys(): string[] {
		if (!this.checklist) {return [];}
		return Object.keys(this.checklist).filter(k => this.vendorBlockedReason(k) !== null);
	}

	get hasBlockedVendors(): boolean {
		return this.blockedVendorKeys.length > 0;
	}

	/** Numeric sanitize on bal / disc inputs — clamp to [0, original outstanding]. */
	onMoneyInput(transaction: any, field: 'bal' | 'disc', vendorKey: string): void {
		const raw = String(transaction[field] ?? '').replace(/[^0-9.\-]/g, '');
		let n = parseFloat(raw);
		if (!isFinite(n)) {n = 0;}
		if (n < 0) {n = 0;}
		const cap = (transaction.ovamount || 0) + (transaction.ovgst || 0) - (transaction.alloc || 0);
		if (field === 'bal' && cap > 0 && n > cap) {n = cap;}
		transaction[field] = (Math.round(n * 100) / 100).toFixed(2);
		this.updateVendorTotal(vendorKey);
	}

	/** Approve & post — no confirmation prompt; clerk already reviewed the check cards. */
	confirmApprove(): void {
		if (this.actionInProgress || this.workflow === PaymentRunWorkflow.Posted) {return;}
		this.finalizePostings();
	}
	constructor(
		private globalSearchService: GlobalSearchService,
		private paymentsService: PaymentsService,
		private vendorService: VendorService,
		private http: HttpClient,
		private location: Location,
		private formBuilder: FormBuilder,
		private printService: PrintService,
		private route: ActivatedRoute,
		private dialog: MatDialog
	) {
		this.color = this.globalSearchService.getColor();

		this.paymentForm = this.formBuilder.group({
			bankaccount: [''],
			currency: [''],
			override_currency: [''],
			checkNumber: [''],
			postingDate: [new Date()],
			supplierIds: [[] as string[]],
			vendorType: [''],
			includeInactiveVendors: [false],
			paymentDueDate: [checkRunAddDays(new Date(), 15)]
		});
	}

	get filteredVendorSelectList(): { supplierid: string; suppname: string; supptype: string | number }[] {
		const t = (this.paymentForm?.get('vendorType')?.value || '').toString().trim();
		if (!t) {
			return this.vendorSelectList;
		}
		return this.vendorSelectList.filter(v => String(v.supptype) === t);
	}

	get searchedVendorSelectList(): { supplierid: string; suppname: string; supptype: string | number }[] {
		const q = this.vendorSelectSearch.trim().toLowerCase();
		if (!q) {
			return this.filteredVendorSelectList;
		}
		return this.filteredVendorSelectList.filter(v => {
			return `${v.supplierid} ${v.suppname}`.toLowerCase().includes(q);
		});
	}

	selectAllVendorSelectOptions(): void {
		this.paymentForm.get('supplierIds')?.setValue(this.filteredVendorSelectList.map(v => v.supplierid));
	}

	selectNoVendorSelectOptions(): void {
		this.paymentForm.get('supplierIds')?.setValue([]);
	}

	ngOnInit(): void {
		this.globalSearchService.configsubscription.subscribe(c => { this.config = c; });
		this.config = this.globalSearchService.getConfig();
		const payload = [];
		this.paymentsService.getCheckSetup(payload).subscribe(r => {
			this.vendorTypes = r.vendclass || [];
			this.paymentForm.get('checkNumber').setValue(r.CheckNum);
		});
		this.initializeBankAccounts();
		this.initializeCurrencies();
		this.initializePaymentMethods();
		this.fetchTodayPayments();
		this.loadVendorSelectList();
		this.routeSub = this.route.paramMap.subscribe(paramMap => {
			const journalParam = paramMap.get('journal');
			if (journalParam) {
				const journal = /^\d+$/.test(journalParam) ? parseInt(journalParam, 10) : journalParam;
				this.loadRunForEdit(journal);
			} else {
				this.loadedJournal = null;
				this.loadedPostings = null;
				this.resetPaymentRunBannerState();
				this.data = [];
				this.checklist = {};
				this.vendorTotals = {};
			}
		});
	}

	ngOnDestroy(): void {
		if (this.routeSub) {
			this.routeSub.unsubscribe();
		}
	}

	loadRunForEdit(journal: number | string): void {
		this.resetPaymentRunBannerState();
		this.paymentsService.loadRunForEdit({ journal }).subscribe(
			(res: any) => {
				if (!res || !res.success) {
					alert(res?.error || 'Failed to load run.');
					return;
				}
				this.checklist = res.checklist || {};
				this.data = { ...res, checklist: this.checklist };
				this.vendorTotals = res.vendortotals || {};
				// Set first: the bank-account write below triggers a nextchecknum lookup that
				// must not overwrite this run's already-assigned numbers.
				this.loadedJournal = res.journal;
				this.paymentForm.get('bankaccount')?.setValue(res.bankacct || this.paymentForm.get('bankaccount')?.value);
				this.paymentForm.get('currency')?.setValue(res.currency || this.paymentForm.get('currency')?.value);
				this.paymentForm.get('override_currency')?.setValue(res.override_currency || '');
				this.paymentForm.get('checkNumber')?.setValue(res.checkNum || this.paymentForm.get('checkNumber')?.value);
				if (res.defCheckPostDate) {
					const d = this.parseDate(res.defCheckPostDate);
					this.paymentForm.get('postingDate')?.setValue(d || new Date());
				}
				if (res.duedate) {
					const due = this.parseDate(res.duedate);
					if (due) {
						this.paymentForm.get('paymentDueDate')?.setValue(due);
					}
				}
				this.vendorDates = res.date && typeof res.date === 'object' ? { ...res.date } : {};
				this.vendorCheckNumbers = res.vendorCheckNumbers && typeof res.vendorCheckNumbers === 'object' ? { ...res.vendorCheckNumbers } : {};
				// A saved run's numbers are already assigned (and may be on printed stock) —
				// never auto-renumber them out from under the clerk.
				this.vendorCheckNumberEdited = {};
				Object.keys(this.vendorCheckNumbers).forEach(k => {
					if ((this.vendorCheckNumbers[k] || '').trim() !== '') {
						this.vendorCheckNumberEdited[k] = true;
					}
				});
				this.vendorPaymentTypes = res.paymentTypes && typeof res.paymentTypes === 'object' ? { ...res.paymentTypes } : {};
				Object.keys(this.checklist).forEach(vendorKey => {
					this.vendorRowFilters[vendorKey] = { ...this.globalRowFilters };
					const transactions = this.checklist[vendorKey] || [];
					const allSelected = transactions.length > 0 && transactions.every((t: any) => t.selected);
					this.vendorChecks[vendorKey] = allSelected;
				});
				const checklistKeys = Object.keys(this.checklist || {});
				if (checklistKeys.length) {
					this.paymentForm.get('supplierIds')?.setValue(checklistKeys);
				}
				this.loadedPostings = res.postings || null;
				this.updateTotals();
			},
			(err: any) => {
				alert(err?.error?.error || 'Failed to load run.');
			}
		);
	}

	loadVendorSelectList(): void {
		this.loadingVendors = true;
		this.vendorService.getVendorsPost({ inc_disabled: !!this.paymentForm.get('includeInactiveVendors')?.value }).subscribe({
			next: (rows: any[]) => {
				this.loadingVendors = false;
				const list = Array.isArray(rows) ? rows : [];
				const seen = new Set<string>();
				this.vendorSelectList = [];
				for (const v of list) {
					const supplierid = String(v?.supplierid ?? '').trim();
					if (!supplierid || seen.has(supplierid)) {
						continue;
					}
					seen.add(supplierid);
					this.vendorSelectList.push({
						supplierid,
						suppname: String(v?.suppname ?? ''),
						supptype: v?.supptype != null ? v.supptype : ''
					});
				}
				this.syncSupplierSelectionWithList();
			},
			error: () => {
				this.loadingVendors = false;
				this.vendorSelectList = [];
				this.paymentForm.get('supplierIds')?.setValue([]);
			}
		});
	}

	/** When vendor type filter changes, pre-select all vendors still visible (same behavior as legacy multi-pick). */
	onVendorTypeFilterChange(): void {
		this.syncSupplierSelectionWithList();
	}

	/**
	 * After vendor list or type filter changes: if editing a saved run, keep vendors that appear in the run
	 * and are still in the filtered list; otherwise select all visible vendors.
	 */
	private syncSupplierSelectionWithList(): void {
		if (this.loadedJournal != null && this.checklist && typeof this.checklist === 'object' && Object.keys(this.checklist).length) {
			const allowed = new Set(this.filteredVendorSelectList.map(v => v.supplierid));
			const fromRun = Object.keys(this.checklist).filter(id => allowed.has(id));
			this.paymentForm.get('supplierIds')?.setValue(fromRun.length ? fromRun : this.filteredVendorSelectList.map(v => v.supplierid));
			return;
		}
		this.paymentForm.get('supplierIds')?.setValue(this.filteredVendorSelectList.map(v => v.supplierid));
	}

	initializeBankAccounts() {
		// Registered before the account list lands so the very first selection also pulls
		// its number — nextchecknum lives on the bank account, not on the screen.
		this.paymentForm.get('bankaccount')?.valueChanges.subscribe((acct: string) => {
			this.loadNextCheckNumber(acct);
		});
		this.globalSearchService.bankaccounts.subscribe((accounts: any) => {
			this.bankAccounts = accounts || [];
			if (this.bankAccounts.length && !this.paymentForm.get('bankaccount')?.value) {
				this.paymentForm.get('bankaccount')?.setValue(this.bankAccounts[0].accountcode);
			}
		});
	}

	/**
	 * Pull bankaccounts.nextchecknum for the selected account into "First check #" —
	 * a different account means a different check book. Skipped for a saved run, whose
	 * numbers are already assigned, and when the account reports no sequence (0/blank),
	 * which leaves the clerk to type one rather than silently starting at 1001.
	 */
	private loadNextCheckNumber(accountcode: string): void {
		if (!accountcode || accountcode === '__ADJ__' || this.loadedJournal != null) {return;}
		this.paymentsService.getCheckSetup({ bankactdef: accountcode }).subscribe((r: any) => {
			const next = r && r.CheckNum != null ? String(r.CheckNum).trim() : '';
			if (next === '' || next === '0') {return;}
			this.paymentForm.get('checkNumber')?.setValue(next);
			this.renumberVendorChecks();
		});
	}

	/** Reload bank accounts when vendor currency or override payment currency changes. */
	reloadBankAccounts(): void {
		const vendorCurr = (this.paymentForm.get('currency')?.value || '').trim();
		const overrideCurr = (this.paymentForm.get('override_currency')?.value || '').trim();
		if (!vendorCurr) {return;}
		this.paymentsService.getBankAccountsForCurrency(vendorCurr, overrideCurr || null).subscribe({
			next: (res: any) => {
				if (res && Array.isArray(res.bankAccounts)) {
					this.bankAccounts = res.bankAccounts;
					const current = this.paymentForm.get('bankaccount')?.value;
					const stillValid = this.bankAccounts.some((b: any) => b.accountcode === current);
					if (!stillValid) {
						this.paymentForm.get('bankaccount')?.setValue(this.bankAccounts.length ? this.bankAccounts[0].accountcode : '');
					}
				}
			},
			error: () => { /* leave existing list intact on error */ }
		});
	}

	initializeCurrencies() {
		this.globalSearchService.currencies.subscribe((currencies: any) => {
			this.currencies = currencies;
			if (currencies && currencies.length) {
				this.globalSearchService.company.subscribe((company: any) => {
					if (company) {
						this.paymentForm.get('currency')?.setValue(company.currencydefault);
					}
				});
			}
		});
	}

	initializePaymentMethods() {
		this.globalSearchService.payment_methods.subscribe((paymentMethods: any) => {
			this.paymentMethods = paymentMethods || [];
		});
	}

	private normalizePaymentName(s: string): string {
		return (s || '').toLowerCase().replace(/[.\s\-_]/g, '');
	}

	getDefaultPaymentType(): string {
		const cfg = this.config || this.globalSearchService.getConfig();
		const configuredDefault = (cfg?.vendorCheckPrintDefaultPaymentType || '').trim();
		const useEftDefault = configuredDefault === 'EFT' || configuredDefault === 'E-Transfer' || cfg?.env?.client === 'km' || cfg?.env?.client === 'summit';
		const target = useEftDefault
			? (configuredDefault === 'E-Transfer' ? 'etransfer' : 'eft')
			: this.normalizePaymentName(configuredDefault);
		if (target && this.paymentMethods && this.paymentMethods.length) {
			const match = this.paymentMethods.find((m: any) => {
				const name = this.normalizePaymentName(m.paymentname || '');
				return name === target || name.includes(target) || target.includes(name);
			});
			if (match) {
				return match.paymentname || 'Check';
			}
		}
		if (this.paymentMethods && this.paymentMethods.length) {
			return this.paymentMethods[0].paymentname || 'Check';
		}
		return 'Check';
	}

	fetchTodayPayments() {
		const today = checkRunLocalYmd(new Date());
		this.paymentsService.getPayments(today).subscribe((results: any) => {
			this.dayPayments = results;

		});
	}

	back(): void {
		this.location.back();
	}

	searching = false;

	/** Opt-in: load the reconciled vendor-statement snapshot from payment_run
	 *  instead of the live ledger. */
	searchStatement() {
		this.searchChecks(true);
	}

	/** Load + select exactly what's due now (Past Due + Current = Total Amount Due).
	 *  Credits net against invoices; the selected net equals the statement's Total Amount Due. */
	selectAmountDue() {
		this.searchChecks(true, 'due');
	}

	searchChecks(statementMode = false, statementScope = '') {
		const raw = this.paymentForm.get('supplierIds')?.value;
		const supplierIds = (Array.isArray(raw) ? raw : []).filter(id => (id || '').toString().trim() !== '');
		if (!supplierIds.length) {
			alert('Select at least one vendor (open the vendor list and use Select All/None if needed).');
			return;
		}
		// Protect a generated-but-not-posted run from being clobbered by a stray search.
		if (this.workflow === PaymentRunWorkflow.Generated) {
			const journal = this.responsePayload?.journal ?? this.loadedJournal ?? '?';
			if (!confirm(`Run #${journal} is generated but not posted.\n\nRunning a new search discards the check preview (the pending run stays — open it from Pending payment runs). Continue?`)) {
				return;
			}
			this.checks = [];
			this.responsePayload = [];
			this.loadedJournal = null;
			this.resetPaymentRunBannerState();
		}
		const vendorTypeValue = (this.paymentForm.get('vendorType')?.value && this.paymentForm.get('vendorType')?.value.toString().trim()) || '';
		const postingVal = this.paymentForm.get('postingDate')?.value;
		const postingDateStr = postingVal instanceof Date ? this.formatDateForPicker(postingVal) : (this.postingDate || '');
		const searchParams = {
			supplierIds,
			vendorType: vendorTypeValue,
			currency: this.paymentForm.get('currency')?.value,
			includeDiscount: this.includeDiscount,
			dueDate: this.paymentDueDateYmd(),
			postingDate: postingDateStr,
			statement: statementMode ? 1 : 0,
			statementScope: statementMode ? statementScope : ''
		};
		this.searching = true;
		this.paymentsService.searchChecks(searchParams).subscribe({
			next: (results: any) => {
				this.searching = false;
				if (!results || results.success === false) {
					const e = results?.error;
					const msg = e
						? (typeof e === 'object' ? Object.values(e).filter(Boolean).join(' ') : String(e))
						: 'Search failed.';
					alert(msg);
					this.checkData = {};
					this.checklist = {};
					this.data = { checklist: this.checklist };
					this.vendorTotals = {};
					this.statementSummary = {};
					this.updateTotals();
					return;
				}
				this.checkData = results;
				this.checklist = results.checklist || {};
				this.data = { ...results, checklist: this.checklist };
				this.vendorTotals = results.vendortotals || {};
				// Statement header figures (Past Due / Current / Total Amount Due) when
				// loaded via "Load statement view"; empty for normal live searches.
				this.statementSummary = results.statementSummary || {};
				// Pre-format raw numerics so inputs read "9,000.00" not "9000" before any blur.
				Object.values(this.checklist).forEach((rows: any) => {
					(rows as any[]).forEach((r: any) => {
						r.bal = this.toMoneyStr(r.bal);
						r.disc = this.toMoneyStr(r.disc);
					});
				});
				this.resetPaymentRunBannerState();
				// Auto-collapse filters once results land — clerk works in the grid below.
				// Re-opened from the summary strip's Edit filters button when needed.
				if (Object.keys(this.checklist).length) {
					this.filtersExpanded = false;
				}

				// Fresh search: drop selection state from any previous/discarded run.
				this.vendorChecks = {};
				this.expandedVendors = {};
				this.vendorCheckNumbers = {};
				this.vendorCheckNumberEdited = {};
				const vendorKeys = Object.keys(this.checklist);
				const autoCollapse = vendorKeys.length > 3;
				vendorKeys.forEach((vendorKey, index) => {
					this.vendorRowFilters[vendorKey] = { ...this.globalRowFilters };
					this.vendorDates[vendorKey] = this.data.defCheckPostDate;
					this.vendorPaymentTypes[vendorKey] = this.getDefaultPaymentType();
					const transactions = this.checklist[vendorKey] || [];
					this.vendorChecks[vendorKey] = transactions.length > 0 && transactions.every((t: any) => t.selected);
					// Auto-collapse all but the first vendor when there are many; keep all open for small runs.
					this.expandedVendors[vendorKey] = !autoCollapse || index === 0;
				});
				// "Select Amount Due": select every loaded row so the net = Total Amount Due.
				if (statementScope === 'due') {
					Object.keys(this.checklist).forEach(vendorKey => {
						(this.checklist[vendorKey] || []).forEach((t: any) => { t.selected = true; });
						this.vendorChecks[vendorKey] = true;
					});
				}
				this.updateTotals();
			},
			error: () => {
				this.searching = false;
				alert('Search failed. Check your connection and try again.');
				this.checkData = {};
				this.checklist = {};
				this.data = { checklist: this.checklist };
				this.vendorTotals = {};
				this.updateTotals();
			}
		});
	}

	updateTotals(): void {
		this.totalDue = 0;
		this.totalDiscount = 0;
		this.selectedTransactions = 0;
		if (!this.checklist || typeof this.checklist !== 'object') {return;}
		Object.keys(this.checklist).forEach(_vendorKey => {
			const transactions = this.checklist[_vendorKey] || [];
			let vendorSelected = 0;
			transactions.forEach((transaction: any) => {
				if (transaction.selected) {
					const bal = parseFloat(transaction.bal || 0);
					this.totalDue += bal;
					this.totalDiscount += parseFloat(transaction.disc || 0);
					this.selectedTransactions++;
					vendorSelected += bal;
				}
			});
			// Keep vendorTotals in sync with actual selection so vendorBlockedReason()
			// reflects what the clerk has checked, not the server's all-rows sum.
			this.vendorTotals[_vendorKey] = vendorSelected;
		});
		// Selection drives which vendors get a check, so the numbers move with it.
		this.renumberVendorChecks();
	}

	updateVendorTotal(vendorKey: string): void {
		this.updateTotals();
	}


	refreshVendor(vendor: string): void {
		// Logic to refresh vendor-specific data
	}
	removeVendor(vendor: string): void {
		delete this.checklist[vendor];
		delete this.vendorTotals[vendor];
		delete this.vendorPaymentTypes[vendor];
		delete this.vendorDates[vendor];
		delete this.vendorCheckNumbers[vendor];
		delete this.vendorChecks[vendor];
		delete this.vendorRowFilters[vendor];
		delete this.vendorSearchTerms[vendor];
		this.updateTotals();
	}
	isDueSoon(dueDate: string): boolean {
		if (!dueDate) {return false;}
		const today = new Date();
		today.setHours(0, 0, 0, 0);
		const due = this.parseDate(dueDate);
		if (!due) {return false;}
		due.setHours(0, 0, 0, 0);
		const daysDifference = Math.floor((due.getTime() - today.getTime()) / (1000 * 3600 * 24));
		return daysDifference <= 14 && daysDifference >= 0;
	}

	parseDate(s: string | null | undefined): Date | null {
		if (s == null || s === '') {
			return null;
		}
		const str = String(s).trim();
		const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(str);
		if (m) {
			const y = +m[1];
			const mo = +m[2] - 1;
			const day = +m[3];
			const d = new Date(y, mo, day);
			return isNaN(d.getTime()) ? null : d;
		}
		const d = new Date(str);
		if (isNaN(d.getTime())) {
			return null;
		}
		return new Date(d.getFullYear(), d.getMonth(), d.getDate());
	}

	/** API expects Y-m-d for due-date filter. */
	paymentDueDateYmd(): string {
		const d = this.paymentForm.get('paymentDueDate')?.value;
		if (!d || !(d instanceof Date) || isNaN(d.getTime())) {
			return '';
		}
		return checkRunLocalYmd(d);
	}

	formatDateForPicker(d: Date): string {
		return d ? checkRunLocalYmd(d) : '';
	}


	updateGroupCheck(vendorKey: string): void {
	  const isChecked = this.vendorChecks[vendorKey];
	  const transactions = this.visibleVendorTransactions(vendorKey, this.checklist[vendorKey] || []);
	  transactions.forEach((transaction: any) => {
			if (this.isPaidTxn(transaction)) { transaction.selected = false; return; }
			transaction.selected = isChecked;
	  });
	  this.updateVendorTotal(vendorKey);
	}

	setAllVendorsSelection(selected: boolean): void {
		if (!this.checklist || typeof this.checklist !== 'object') {return;}
		Object.keys(this.checklist).forEach((vendorKey: string) => {
			this.vendorChecks[vendorKey] = selected;
			const transactions = this.checklist[vendorKey] || [];
			transactions.forEach((t: any) => { t.selected = this.isPaidTxn(t) ? false : selected; });
		});
		this.updateTotals();
	}

	allVendorsSelected(): boolean {
		if (!this.checklist || typeof this.checklist !== 'object') {return false;}
		const keys = Object.keys(this.checklist);
		if (!keys.length) {return false;}
		return keys.every((vendorKey: string) => {
			const transactions = this.checklist[vendorKey] || [];
			return transactions.length > 0 && transactions.every((t: any) => t.selected);
		});
	}

	updateCheckDate(vendorKey: string): void {
	}

	onVendorCheckDateChange(vendorKey: string, d: Date | null): void {
		this.vendorDates[vendorKey] = d ? this.formatDateForPicker(d) : '';
		this.updateCheckDate(vendorKey);
	}

	onCheckNumberChange() {
		this.renumberVendorChecks();
	}

	/** The clerk typed a Ref # for this vendor — it is theirs now, auto numbering leaves it alone. */
	onVendorCheckNumberChange(vendorKey: string): void {
		this.vendorCheckNumberEdited[vendorKey] = true;
		this.renumberVendorChecks();
	}

	/**
	 * Hand out check numbers from "First check #", in vendor order, ONLY to vendors that
	 * will actually produce a check. Vendors with nothing selected must not consume a
	 * number — the server only writes a check where the net is non-zero, so burning one
	 * here would leave a gap between the printed checks and the stock in the printer.
	 * Numbers the clerk typed are preserved and skipped over.
	 */
	private renumberVendorChecks(): void {
		const start = parseInt(this.paymentForm.get('checkNumber')?.value || '', 10);
		const claimed = new Set(
			Object.keys(this.vendorCheckNumbers)
				.filter(k => this.vendorCheckNumberEdited[k])
				.map(k => (this.vendorCheckNumbers[k] || '').trim())
				.filter(v => v !== '')
		);
		let next = isFinite(start) && start > 0 ? start : null;
		Object.keys(this.checklist || {}).forEach(vendorKey => {
			if (this.vendorCheckNumberEdited[vendorKey]) {return;}
			if (!this.vendorGetsCheck(vendorKey)) {
				delete this.vendorCheckNumbers[vendorKey];
				return;
			}
			if (next === null) {
				this.vendorCheckNumbers[vendorKey] = '';
				return;
			}
			while (claimed.has(String(next))) {next++;}
			this.vendorCheckNumbers[vendorKey] = String(next);
			next++;
		});
	}

	/** True when this vendor's current selection would produce a check (server: net != 0). */
	private vendorGetsCheck(vendorKey: string): boolean {
		const rows = (this.checklist?.[vendorKey] || []) as any[];
		if (!rows.some(r => r.selected)) {return false;}
		return (Number(this.vendorTotals?.[vendorKey]) || 0) !== 0;
	}

	/** Blocks Save/Generate when the numbers that would be printed are unusable. */
	checkNumberIssue(): string | null {
		const paying = Object.keys(this.checklist || {}).filter(k => this.vendorGetsCheck(k));
		if (!paying.length) {return null;}
		const seen: { [ref: string]: string } = {};
		for (const key of paying) {
			const ref = (this.vendorCheckNumbers[key] || '').trim();
			if (!ref) {
				return `${key} has no Ref # — set "First check #" or type a number on the vendor row.`;
			}
			if (seen[ref]) {
				return `Check # ${ref} is on both ${seen[ref]} and ${key} — give each vendor its own Ref #.`;
			}
			seen[ref] = key;
		}
		return null;
	}

	private resetPaymentRunBannerState(): void {
		this.workflow = PaymentRunWorkflow.Initial;
		this.bannerMessage = null;
		this.savedForReviewJournal = null;
		this.postedResult = null;
		this.lastPostedRunJournal = null;
	}

	/** Flip a generated (not yet posted) run back to the edit screen with all selections intact.
	    The run stays pending and the next Generate reuses the same journal (reprint). */
	backToEditRun(): void {
		if (this.actionInProgress || this.workflow === PaymentRunWorkflow.Posted) {
			return;
		}
		const journal = this.responsePayload?.journal ?? this.loadedJournal;
		if (journal == null || String(journal) === '') {
			return;
		}
		this.checks = [];
		this.responsePayload = [];
		this.loadRunForEdit(journal);
	}

	/** Abandon a generated (not yet posted) run: removes the pending run row so it cannot be posted. */
	discardGeneratedRun(): void {
		if (this.actionInProgress || this.workflow === PaymentRunWorkflow.Posted) {
			return;
		}
		const journal = this.responsePayload?.journal ?? this.loadedJournal;
		if (journal == null || String(journal) === '') {
			return;
		}
		if (!confirm('Discard this generated run (journal #' + journal + ')? Nothing has been posted; the check preview and pending run entry will be removed.')) {
			return;
		}
		this.actionInProgress = true;
		this.paymentsService.discardRun({ journal }).subscribe(
			(res: any) => {
				this.actionInProgress = false;
				if (res && res.success === false) {
					alert(res.error || 'Could not discard the run — check Pending payment runs.');
					return;
				}
				this.checks = [];
				this.responsePayload = [];
				this.loadedJournal = null;
				this.resetPaymentRunBannerState();
				this.globalSearchService.showNotification('Run discarded — nothing was posted.', 'success', 'bottom', 'right');
				// Drop the clerk back into the invoice list — only if vendor selection survived.
				// (Some workflows clear the form on workflow reset; don't run an empty search.)
				const rawIds = this.paymentForm.get('supplierIds')?.value;
				const ids = (Array.isArray(rawIds) ? rawIds : []).filter(v => (v || '').toString().trim() !== '');
				if (ids.length) {
					this.searchChecks();
				}
			},
			() => {
				this.actionInProgress = false;
				alert('Could not discard the run. It may have been posted or already removed — check Pending payment runs.');
			}
		);
	}

	private isRunAlreadyPostedInSession(journal: any): boolean {
		if (journal == null || String(journal).trim() === '') {
			return false;
		}
		if (this.lastPostedRunJournal == null || String(this.lastPostedRunJournal).trim() === '') {
			return false;
		}
		return String(this.lastPostedRunJournal) === String(journal);
	}

	/** Same journal + non-empty postings as generate payment run — safe to keep check cards / PDF. */
	private canReusePreviewAfterPost(postedJournal: number | string): boolean {
		const r = this.responsePayload;
		if (!r || typeof r !== 'object' || Array.isArray(r)) {
			return false;
		}
		if (String(r.journal ?? '') !== String(postedJournal)) {
			return false;
		}
		return hasPaymentRunPostings(r.postings);
	}

	/** After finalize: keep or rebuild check preview (like Generate payment run); do not redirect away. */
	private applyPostSuccess(results: any, finalizeCb: () => void): void {
		this.workflow = PaymentRunWorkflow.Posted;
		this.postedResult = results;
		if (results.journal != null && String(results.journal) !== '') {
			this.lastPostedRunJournal = results.journal;
		}
		this.bannerMessage = formatPaymentRunPostedMessage(results);
		this.globalSearchService.showNotification(this.bannerMessage, 'success', 'bottom', 'right');
		this.savedForReviewJournal = null;

		const postedJournal = results.journal;
		const reuse =
			postedJournal != null &&
			String(postedJournal) !== '' &&
			this.canReusePreviewAfterPost(postedJournal);

		const finish = (): void => {
			this.loadedJournal = null;
			this.loadedPostings = null;
			finalizeCb();
		};

		if (reuse) {
			finish();
			return;
		}
		if (postedJournal == null || String(postedJournal) === '') {
			finish();
			return;
		}

		const payload: any = {
			...this.buildSavePayload(),
			reprint: 'true',
			journal: postedJournal
		};
		const previewChecks = this.checks;
		const previewPayload = this.responsePayload;
		this.actionInProgress = true;
		this.paymentsService.postChecks(payload).subscribe({
			next: (res: any) => {
				this.actionInProgress = false;
				const incoming = res?.checks;
				const hasChecks =
					incoming &&
					typeof incoming === 'object' &&
					!Array.isArray(incoming) &&
					Object.keys(incoming).length > 0;
				if (hasChecks) {
					this.responsePayload = res;
					this.formatChecks();
				}
				if (!hasChecks && previewChecks?.length) {
					this.responsePayload = previewPayload;
					this.checks = previewChecks;
				}
				finish();
			},
			error: () => {
				this.actionInProgress = false;
				if (previewChecks?.length) {
					this.responsePayload = previewPayload;
					this.checks = previewChecks;
				}
				this.globalSearchService.showNotification(
					'Payments posted; check/PDF preview could not be refreshed from saved run.',
					'warning',
					'bottom',
					'right'
				);
				finish();
			}
		});
	}

	/** Convert the server's postings object (keyed by checknum) to a flat array. */
	private postingsToArray(postings: any): any[] {
		if (!postings) {return [];}
		if (Array.isArray(postings)) {return postings.filter(p => p);}
		return Object.values(postings).filter((p: any) => p);
	}

	/** Send postings in chunks of POST_CHUNK_SIZE. Updates this.postingProgress
	    live so the sticky footer can render a progress bar. Resolves with the
	    final server response (done + summary) once the last chunk closes. */
	private async postInChunks(journal: string | number, postings: any): Promise<any> {
		const list = this.postingsToArray(postings);
		const total = list.length;
		this.postingProgress = { current: 0, total, posted: 0, skipped: 0, errors: [] };

		let lastResp: any = { success: true, done: false };
		for (let i = 0; i < total; i += this.POST_CHUNK_SIZE) {
			const chunk = list.slice(i, i + this.POST_CHUNK_SIZE);
			const isLast = (i + chunk.length) >= total;

			let resp: any = null;
			try {
				resp = await this.paymentsService.postChecksChunk({
					journal,
					chunk,
					startIndex: i,
					totalCount: total,
					isLast
				}).toPromise();
			} catch (err: any) {
				const msg = err?.error?.error || err?.message || 'Network error during posting.';
				this.postingProgress.errors.push({ chunkStart: i, error: msg });
				throw new Error(msg);
			}

			if (!resp || resp.success === false) {
				const msg = resp?.error || 'Server rejected chunk.';
				this.postingProgress.errors.push({ chunkStart: i, error: msg });
				throw new Error(msg);
			}

			this.postingProgress.posted  += Number(resp.posted)  || 0;
			this.postingProgress.skipped += Number(resp.skipped) || 0;
			this.postingProgress.current = Math.min(i + chunk.length, total);
			if (Array.isArray(resp.errors) && resp.errors.length) {
				this.postingProgress.errors.push(...resp.errors);
				// Abort the rest of the run rather than partially post — clerk has to fix the
				// failing posting(s) and Approve again (already-posted items skip idempotently).
				throw new Error(`Posting failed on ${resp.errors.length} item(s). ` + (resp.errors[0]?.error || ''));
			}
			lastResp = resp;
		}

		return lastResp;
	}

	finalizePostings(): void {
		if (this.actionInProgress) {return;}
		if (this.workflow === PaymentRunWorkflow.Posted) {
			this.globalSearchService.showNotification(
				'This payment run is already posted. Run Search to start a new run, or open another pending run.',
				'warning',
				'bottom',
				'right'
			);
			return;
		}
		const previewFinalize = this.loadedJournal == null ? this.getFinalizePayloadFromPreview() : null;
		if (previewFinalize && this.isRunAlreadyPostedInSession(previewFinalize.journal)) {
			alert(
				'This payment run (journal #' +
					previewFinalize.journal +
					') was already posted in this session. Run Search to start a new payment run.'
			);
			return;
		}
		this.actionInProgress = true;
		this.workflow = PaymentRunWorkflow.Posting;
		const done = () => {
			this.actionInProgress = false;
			if (this.workflow === PaymentRunWorkflow.Posting) {
				this.workflow = this.checks?.length ? PaymentRunWorkflow.Generated : PaymentRunWorkflow.Initial;
			}
		};

		// Fast path: when Generate has just produced postings in memory, post them
		// directly. Saving the (now-hidden) form selections again would clobber the
		// run with an empty selection and the server would reject with
		// "No transactions selected." — that bug previously fired here.
		const inMemory = this.getFinalizePayloadFromPreview();
		if (inMemory && hasPaymentRunPostings(inMemory.postings)) {
			this.postInChunks(inMemory.journal, inMemory.postings).then(
				(_resp: any) => {
					const list = this.postingsToArray(inMemory.postings);
					const totalAmount = list.reduce((s: number, p: any) => s + (Number(p?.AccumBalance) || 0), 0);
					const currency = (list.find((p: any) => p?.Currency)?.Currency) || 'USD';
					const vendorIds = new Set(list.map((p: any) => p?.SupplierID).filter(Boolean));
					this.applyPostSuccess({
						success: true,
						journal: inMemory.journal,
						summary: {
							totalAmount,
							currency,
							vendorCount: vendorIds.size || list.length,
							paymentCount: list.length
						}
					}, () => done());
					this.postingProgress = null;
				},
				(err: any) => {
					this.globalSearchService.showNotification(
						(err?.message || 'There was an error processing the payments.') +
						(this.postingProgress?.posted ? ` (${this.postingProgress.posted} already posted will be skipped on retry)` : ''),
						'error', 'bottom', 'right'
					);
					done();
				}
			);
			return;
		}

		if (this.loadedJournal != null) {
			this.paymentsService.savePaymentRunForReview(this.buildSavePayload()).subscribe(
				(saveRes: any) => {
					if (!saveRes.success) {
						alert(saveRes.error || 'Failed to save.');
						done();
						return;
					}
					const runJournal =
						saveRes.journal !== undefined && saveRes.journal !== null && String(saveRes.journal) !== ''
							? saveRes.journal
							: this.loadedJournal;
					this.paymentsService.loadRunData({ journal: runJournal }).subscribe(
						(loadRes: any) => {
							if (!loadRes || !loadRes.success || !hasPaymentRunPostings(loadRes.postings)) {
								const emptyPostings = loadRes?.success && !hasPaymentRunPostings(loadRes?.postings);
								const detail = emptyPostings
									? 'This run has no payment lines to post (empty postings in saved data). Re-open the run from Pending payment runs, adjust selections, use Save for review, then Approve & post again. If it keeps happening, run Generate payment run once before posting.'
									: loadRes?.error || 'Failed to load run for posting.';
								alert(
									detail +
										(saveRes.journal != null && saveRes.journal !== ''
											? ` (run journal #${saveRes.journal})`
											: '')
								);
								done();
								return;
							}
							if (this.isRunAlreadyPostedInSession(runJournal)) {
								alert(
									'This payment run (journal #' +
										runJournal +
										') was already posted in this session. Run Search to start a new payment run, or open a different pending run.'
								);
								done();
								return;
							}
							this.postInChunks(runJournal, loadRes.postings).then(
								(finalResp: any) => {
									// Mirror the legacy applyPostSuccess shape — synthesize a results
									// object since the chunked endpoint doesn't repeat all the summary data.
									const list = this.postingsToArray(loadRes.postings);
									const totalAmount = list.reduce((s: number, p: any) => s + (Number(p?.AccumBalance) || 0), 0);
									const currency = (list.find((p: any) => p?.Currency)?.Currency) || 'USD';
									const vendorIds = new Set(list.map((p: any) => p?.SupplierID).filter(Boolean));
									this.applyPostSuccess({
										success: true,
										journal: runJournal,
										summary: {
											totalAmount,
											currency,
											vendorCount: vendorIds.size || list.length,
											paymentCount: list.length
										}
									}, () => done());
									this.postingProgress = null;
								},
								(err: any) => {
									this.globalSearchService.showNotification(
										(err?.message || 'Failed to post.') +
										(this.postingProgress?.posted ? ` (${this.postingProgress.posted} already posted will be skipped on retry)` : ''),
										'error', 'bottom', 'right'
									);
									done();
								}
							);
						},
						() => {
							alert('Failed to load run for posting.');
							done();
						}
					);
				},
				() => {
					alert('Failed to save run.');
					done();
				}
			);
			return;
		}
		const finalizePayload = this.getFinalizePayloadFromPreview();
		if (!finalizePayload) {
			done();
			alert(
				'Nothing to post yet. Use the step that generates checks (Create Print / preview) so journal and payment lines exist, then click Approve & post again.\n\n' +
					'Vendor remittance emails are turned on under Vendors → Remittance email (not on this screen).'
			);
			return;
		}
		this.postInChunks(finalizePayload.journal, finalizePayload.postings).then(
			(_resp: any) => {
				const list = this.postingsToArray(finalizePayload.postings);
				const totalAmount = list.reduce((s: number, p: any) => s + (Number(p?.AccumBalance) || 0), 0);
				const currency = (list.find((p: any) => p?.Currency)?.Currency) || 'USD';
				const vendorIds = new Set(list.map((p: any) => p?.SupplierID).filter(Boolean));
				this.applyPostSuccess({
					success: true,
					journal: finalizePayload.journal,
					summary: {
						totalAmount,
						currency,
						vendorCount: vendorIds.size || list.length,
						paymentCount: list.length
					}
				}, () => done());
				this.postingProgress = null;
			},
			(error: any) => {
				this.globalSearchService.showNotification(
					(error?.message || 'There was an error processing the payments.') +
					(this.postingProgress?.posted ? ` (${this.postingProgress.posted} already posted will be skipped on retry)` : ''),
					'error', 'bottom', 'right'
				);
				done();
			}
		);
	}

	/**
	 * After Generate payment run, uses server postings; otherwise builds from the checklist
	 * (selected lines, bal + disc — same as Save for review / Generate payment run).
	 */
	openRemittancePreview(): void {
		const fromPostings = this.getRemitTabsFromFinalizePostings();
		const tabs =
			fromPostings && fromPostings.length
				? fromPostings
				: this.buildRemittanceTabsFromChecklist();
		if (!tabs || !tabs.length) {
			this.globalSearchService.showNotification(
				'Select at least one invoice line, or run Generate payment run first.',
				'info',
				'bottom',
				'right'
			);
			return;
		}
		const title =
			fromPostings && fromPostings.length
				? 'Remittance email preview (from generated payment run)'
				: 'Remittance email preview (from current selection — matches Save for review)';
		this.dialog.open(VendorRemitPreviewModalComponent, {
			width: '760px',
			maxWidth: '98vw',
			data: { title, tabs } as VendorRemitPreviewModalData
		});
	}

	private getRemitTabsFromFinalizePostings(): VendorRemitPreviewTabRequest[] | null {
		const payload = this.getFinalizePayloadFromPreview();
		if (!payload?.postings) {return null;}
		return this.remitTabsFromPostingsObject(payload.postings);
	}

	private remitTabsFromPostingsObject(postings: any): VendorRemitPreviewTabRequest[] {
		const tabs: VendorRemitPreviewTabRequest[] = [];
		Object.keys(postings).forEach((key) => {
			const post = postings[key];
			if (!post || !post.SupplierID) {return;}
			const allocs = (post.Allocs || [])
				.filter((a: any) => (parseFloat(a.Amount) || 0) > 0)
				.map((a: any) => ({
					trans_id: +a.TransID,
					amount: Math.round((parseFloat(a.Amount) || 0) * 100) / 100
				}));
			if (!allocs.length) {return;}
			tabs.push({
				label: `${post.SupplierID} — ${post.PaytReference || key}`,
				supplier_id: String(post.SupplierID),
				context: {
					payment_total: parseFloat(post.AccumBalance) || 0,
					payment_discount: parseFloat(post.AccumDisc) || 0,
					payment_reference: post.PaytReference || '',
					checkno: post.PaytReference || '',
					payment_type: post.PaytType || '',
					post_date: post.PostDate || '',
					bank_account: post.BankAcct || '',
					currency: post.Currency || 'USD'
				},
				allocations: allocs
			});
		});
		return tabs;
	}

	private buildRemittanceTabsFromChecklist(): VendorRemitPreviewTabRequest[] | null {
		if (!this.checklist || typeof this.checklist !== 'object') {return null;}
		const bank = this.paymentForm.get('bankaccount')?.value || '';
		const currency = this.paymentForm.get('currency')?.value || 'USD';
		const formDate = this.paymentForm.get('postingDate')?.value;
		const defaultPostDate = formDate instanceof Date ? this.formatDateForPicker(formDate) : this.postingDate;
		const tabs: VendorRemitPreviewTabRequest[] = [];
		Object.keys(this.checklist).forEach((vendorKey) => {
			const transactions = this.checklist[vendorKey] || [];
			const allocs: Array<{ trans_id: number; amount: number }> = [];
			let accum = 0;
			let accumDisc = 0;
			transactions.forEach((t: any) => {
				if (!t.selected) {return;}
				const bal = parseFloat(t.bal) || 0;
				const disc = parseFloat(t.disc) || 0;
				const lineTotal = Math.round((bal + disc) * 100) / 100;
				if (lineTotal > 0 && t.id != null) {
					allocs.push({ trans_id: +t.id, amount: lineTotal });
					accum += bal + disc;
					accumDisc += disc;
				}
			});
			if (!allocs.length) {return;}
			const postDate = this.vendorDates[vendorKey] || defaultPostDate;
			const ref = (this.vendorCheckNumbers[vendorKey] || '').trim();
			tabs.push({
				label: `${vendorKey} — ${ref || 'ref'}`,
				supplier_id: String(vendorKey),
				context: {
					payment_total: Math.round(accum * 100) / 100,
					payment_discount: Math.round(accumDisc * 100) / 100,
					payment_reference: ref,
					checkno: ref,
					payment_type: this.vendorPaymentTypes[vendorKey] || '',
					post_date: postDate,
					bank_account: bank,
					currency
				},
				allocations: allocs
			});
		});
		return tabs.length ? tabs : null;
	}

	private getFinalizePayloadFromPreview(): { journal: any; postings: any } | null {
		const r = this.responsePayload;
		if (!r || typeof r !== 'object' || Array.isArray(r)) {
			return null;
		}
		const journal = r.journal;
		const postings = r.postings;
		if (journal === undefined || journal === null || String(journal).trim() === '') {
			return null;
		}
		if (!postings || typeof postings !== 'object') {
			return null;
		}
		if (paymentRunPostingsCount(postings) === 0) {
			return null;
		}
		return { journal, postings };
	}

	saveForReview(): void {
		if (this.selectedTransactions === 0 || this.actionInProgress) {return;}
		const numIssue = this.checkNumberIssue();
		if (numIssue) {
			this.globalSearchService.showNotification(numIssue, 'error', 'bottom', 'right');
			return;
		}
		this.actionInProgress = true;
		const payload = this.buildSavePayload();
		this.paymentsService.savePaymentRunForReview(payload).subscribe(
			(results: any) => {
				this.actionInProgress = false;
				if (results.success) {
					this.workflow = PaymentRunWorkflow.SavedForReview;
					this.savedForReviewJournal = results.journal;
					this.bannerMessage = `Run #${results.journal} saved for review.`;
					this.globalSearchService.showNotification(this.bannerMessage, 'success', 'bottom', 'right');
					setTimeout(() => {
						if (this.workflow === PaymentRunWorkflow.SavedForReview) {
							this.bannerMessage = null;
							this.savedForReviewJournal = null;
							this.workflow = this.checks?.length ? PaymentRunWorkflow.Generated : PaymentRunWorkflow.Initial;
						}
					}, 8000);
					if (this.loadedJournal != null) {
						this.paymentsService.loadRunForEdit({ journal: this.loadedJournal }).subscribe((refresh: any) => {
							if (refresh && refresh.success && refresh.postings) {
								this.loadedPostings = refresh.postings;
							}
						});
					}
				} else if (results.error) {
					alert(results.error);
				}
			},
			(error: any) => {
				this.actionInProgress = false;
				alert(error?.error?.error || 'Failed to save for review.');
			}
		);
	}

	private buildSavePayload(): any {
		const overrideCurr = (this.paymentForm.get('override_currency')?.value || '').trim();
		return {
			reprint: this.loadedJournal != null ? 'true' : 'false',
			journal: this.loadedJournal != null ? this.loadedJournal : (this.paymentForm.get('checkNumber')?.value || ''),
			checkNum: this.paymentForm.get('checkNumber')?.value || '1001',
			date: this.generateVendorDates(),
			payamt: this.generatePayAmounts(),
			discamt: this.generateDiscountAmounts(),
			bankacct: this.paymentForm.get('bankaccount')?.value || '',
			currency: this.paymentForm.get('currency')?.value || 'USD',
			override_currency: overrideCurr || null,
			duedate: this.paymentDueDateYmd(),
			include: this.generateIncludeMap(),
			vendorCheckNumbers: this.vendorCheckNumbers,
			paymentTypes: this.vendorPaymentTypes
		};
	}

	createPrint(): void {
		if (this.actionInProgress) {return;}
		// Pre-validate blocked vendors — the server would reject these with an alert;
		// surfacing inline lets the clerk fix the rows without a popup interrupt.
		const blocked = this.blockedVendorKeys;
		if (blocked.length) {
			blocked.forEach(k => (this.expandedVendors[k] = true));
			this.globalSearchService.showNotification(
				`${blocked.length} vendor(s) cannot post — see the warning on each affected vendor strip and adjust selections.`,
				'error', 'bottom', 'right'
			);
			return;
		}
		const numIssue = this.checkNumberIssue();
		if (numIssue) {
			this.globalSearchService.showNotification(numIssue, 'error', 'bottom', 'right');
			return;
		}
		this.actionInProgress = true;
		this.workflow = PaymentRunWorkflow.Generating;
		const payload = this.buildSavePayload();
		// Re-generating a loaded run keeps its journal but sends the CURRENT selections
		// (reuseJournal). 'reprint' would resurrect the previously saved data, discarding edits.
		payload.reprint = 'false';
		payload.reuseJournal = this.loadedJournal != null ? 'true' : 'false';
		payload.journal = this.loadedJournal != null ? this.loadedJournal : (this.paymentForm.get('checkNumber')?.value || '');

		this.paymentsService.postChecks(payload).subscribe(
			(results: any) => {
				this.actionInProgress = false;
				if (results && results.success === false) {
					this.workflow = this.checks?.length ? PaymentRunWorkflow.Generated : PaymentRunWorkflow.Initial;
					alert(results.error || 'Could not generate the payment run.');
					return;
				}
				const wasPosted = this.workflow === PaymentRunWorkflow.Posted;
				if (!wasPosted) {
					this.bannerMessage = null;
					this.savedForReviewJournal = null;
					this.postedResult = null;
				}
				this.responsePayload = results;
				this.formatChecks();
				if (!wasPosted) {
					this.workflow = PaymentRunWorkflow.Generated;
				}
				if (this.loadedJournal == null) {
					this.checkData = [];
					this.checklist = {};
					this.data = [];
					this.vendorTotals = {};
					this.totalDue = 0;
				}
				// Adopt the run's journal so Back to edit / Discard / a later regenerate
				// all target this same pending run instead of drifting to a new journal.
				if (results?.journal != null && String(results.journal) !== '') {
					this.loadedJournal = results.journal;
				}
			},
			(error: any) => {
				this.actionInProgress = false;
				if (this.workflow === PaymentRunWorkflow.Generating) {
					this.workflow = this.checks?.length ? PaymentRunWorkflow.Generated : PaymentRunWorkflow.Initial;
				}
				alert(error?.error?.error || 'There was an error processing the payments. Please try again.');
			}
		);
	}

	printCheck() {
		this.globalSearchService.downloadPdf(this.responsePayload?.base64content, this.responsePayload?.checkNum + '-CheckRun.pdf');
	}

	/** Combined full-remittance PDF — one page per check, all invoice lines. */
	downloadRemittances() {
		if (!this.responsePayload?.remitbase64) {
			alert('Remittance PDF is not available for this run. Use Generate payment run again to rebuild it.');
			return;
		}
		this.globalSearchService.downloadPdf(this.responsePayload.remitbase64, this.responsePayload?.journal + '-Remittances.pdf');
	}

	exportChecklistPdf(): void {
		if (!this.checklist || typeof this.checklist !== 'object' || this.exportingChecklistPdf) {return;}
		const html = this.buildChecklistHtml();
		if (!html) {return;}
		this.exportingChecklistPdf = true;
		const encoded = this.globalSearchService.base64encode(html);
		const today = new Date();
		const filename = `payment-run-checklist-${today.toISOString().slice(0, 10)}.pdf`;
		const selectedCount = this.selectedTransactions;
		const subtitle = selectedCount > 0
			? `${selectedCount} selected · Total due $${this.totalDue.toFixed(2)} · Discounts $${this.totalDiscount.toFixed(2)} · ${today.toLocaleString()}`
			: `Created ${today.toLocaleString()}`;
		this.printService.pdf({
			content: encoded,
			filename,
			title: 'Payment Run Checklist (Selected)',
			subtitle
		}).subscribe(
			(result: any) => {
				this.exportingChecklistPdf = false;
				if (result?.content) {
					this.globalSearchService.downloadPdf(result.content, filename);
				} else {
					alert('Could not generate PDF.');
				}
			},
			() => {
				this.exportingChecklistPdf = false;
				alert('Failed to generate PDF.');
			}
		);
	}

	exportChecklistXls(): void {
		if (!this.checklist || typeof this.checklist !== 'object' || this.exportingChecklistXls) {return;}
		const html = this.buildChecklistHtml();
		if (!html) {return;}
		this.exportingChecklistXls = true;
		const encoded = this.globalSearchService.base64encode(html);
		const today = new Date();
		const filename = `payment-run-checklist-${today.toISOString().slice(0, 10)}.xlsx`;
		const selectedCount = this.selectedTransactions;
		const subtitle = selectedCount > 0
			? `${selectedCount} selected · Total due $${this.totalDue.toFixed(2)} · Discounts $${this.totalDiscount.toFixed(2)} · ${today.toLocaleString()}`
			: `Created ${today.toLocaleString()}`;
		this.printService.xls({
			content: encoded,
			filename,
			title: 'Payment Run Checklist (Selected)',
			subtitle
		}).subscribe(
			(result: any) => {
				this.exportingChecklistXls = false;
				if (result?.content) {
					this.globalSearchService.downloadXls(result.content, filename);
				} else {
					alert('Could not generate XLS.');
				}
			},
			() => {
				this.exportingChecklistXls = false;
				alert('Failed to generate XLS.');
			}
		);
	}

	private buildChecklistHtml(): string {
		if (!this.checklist || typeof this.checklist !== 'object') {return '';}
		let html = '<table style="width:100%; border-collapse: collapse;">';
		html += '<thead><tr style="background:#333; color:white;">';
		html += '<th style="padding:4px; text-align:left;">Vendor</th><th style="padding:4px;">Type</th><th style="padding:4px;">Ref</th><th style="padding:4px;">Inv Date</th><th style="padding:4px;">Due Date</th>';
		html += '<th style="padding:4px; text-align:right;">Net</th><th style="padding:4px; text-align:right;">Discount</th><th style="padding:4px; text-align:right;">Amount Due</th></tr></thead><tbody>';
		let totalDue = 0;
		let totalDisc = 0;
		Object.keys(this.checklist).forEach((vendorKey: string) => {
			const transactions = this.checklist[vendorKey] || [];
			const suppname = transactions[0]?.suppname || '';
			transactions.filter((t: any) => t.selected).forEach((t: any) => {
				const net = (parseFloat(t.ovamount) || 0) + (parseFloat(t.ovgst) || 0) + (parseFloat(t.ovfee) || 0);
				const disc = parseFloat(t.disc) || 0;
				const due = parseFloat(t.bal) || 0;
				totalDue += due;
				totalDisc += disc;
				html += '<tr style="border-bottom:1px solid #ddd">';
				html += `<td style="padding:4px">${(vendorKey + ' ' + suppname).replace(/</g, '&lt;')}</td>`;
				html += `<td style="padding:4px">${(t.typename || '').replace(/</g, '&lt;')} (${t.transno || ''})</td>`;
				html += `<td style="padding:4px">${(t.suppreference || '').replace(/</g, '&lt;')}</td>`;
				html += `<td style="padding:4px">${(t.trandate || '').replace(/</g, '&lt;')}</td>`;
				html += `<td style="padding:4px">${(t.duedate || '').replace(/</g, '&lt;')}</td>`;
				html += `<td style="padding:4px; text-align:right">$${net.toFixed(2)}</td>`;
				html += `<td style="padding:4px; text-align:right">$${disc.toFixed(2)}</td>`;
				html += `<td style="padding:4px; text-align:right">$${due.toFixed(2)}</td>`;
				html += '</tr>';
			});
		});
		html += '</tbody><tfoot><tr style="background:#f0f0f0; font-weight:bold; border-top:2px solid #333">';
		html += '<td colspan="6" style="padding:6px; text-align:right">Total selected:</td>';
		html += `<td style="padding:6px; text-align:right">$${totalDisc.toFixed(2)}</td>`;
		html += `<td style="padding:6px; text-align:right">$${totalDue.toFixed(2)}</td></tr></tfoot></table>`;
		return html;
	}
	formatChecks(): void {
		const checks = this.responsePayload?.checks;
		if (checks) {
			this.checks = Object.keys(checks).map((checkNumber) => {
				const checkData = checks[checkNumber];
				return {
					checkNumber,
					postings: checkData.postings,
					check: checkData.check,
					stub: checkData.stub
				};
			});
		}
	}

	// Helper to generate date mapping
	private generateVendorDates(): { [key: string]: string } {
		const formDate = this.paymentForm.get('postingDate')?.value;
		const defaultDate = formDate instanceof Date ? this.formatDateForPicker(formDate) : this.postingDate;
		const dates: { [key: string]: string } = {};
		Object.keys(this.checklist).forEach(vendorKey => {
			dates[vendorKey] = this.vendorDates[vendorKey] || defaultDate;
		});
		return dates;
	}

	// Helper to generate pay amounts mapping
	private generatePayAmounts(): {
		[key: string]: string } {
		const payAmounts: {
			[key: string]: string } = {};
		Object.keys(this.checklist).forEach(vendorKey => {
			this.checklist[vendorKey].forEach((transaction: any) => {
				if (transaction.selected) {
					payAmounts[transaction.id] = transaction.bal || '0.00';
				}
			});
		});
		return payAmounts;
	}

	// Helper to generate discount amounts mapping
	private generateDiscountAmounts(): {
		[key: string]: string } {
		const discountAmounts: {
			[key: string]: string } = {};
		Object.keys(this.checklist).forEach(vendorKey => {
			this.checklist[vendorKey].forEach((transaction: any) => {
				if (transaction.selected) {
					discountAmounts[transaction.id] = transaction.disc || '0.00';
				}
			});
		});
		return discountAmounts;
	}

	// Helper to generate include map
	private generateIncludeMap(): {
		[key: string]: boolean } {
		const includeMap: {
			[key: string]: boolean } = {};
		Object.keys(this.checklist).forEach(vendorKey => {
			this.checklist[vendorKey].forEach((transaction: any) => {
				includeMap[transaction.id] = transaction.selected || false;
			});
		});
		return includeMap;
	}



}
